import {
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import React from 'react';
import { getJwtTokenFromIdToken } from '../auth/AuthFanc';
import { useParams } from 'react-router-dom';
import { fetchErrorText } from '../../../common/const';
import { Save } from '@mui/icons-material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { customDataGridLocaleTextNoData, StyledDataGrid } from '../../../styles/styles';

export type SdcReplacement = {
  createdAt: string;
  autoSdcReplacementId: number;
  sourceSdcUuid: string;
  sourceSdcSerialNumber: string | null;
  destinationSdcUuid: string;
  destinationSdcSerialNumber: string | null;
  currentSdcUuid: string;
  doorPlace: string;
  memo: string;
};

export type SdcReplacementTableProps = {
  sdcReplacements: SdcReplacement[];
  setSdcReplacements: React.Dispatch<React.SetStateAction<SdcReplacement[]>>;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
};

export const SdcReplacementTable: React.FC<SdcReplacementTableProps> = (
  props
) => {
  const { autoBuildingId } = useParams();

  const [updatingSdcReplacementId, setUpdatingSdcReplacementId] =
    React.useState<number>(0);
  const [editedSdcReplacementIdMap, setEditedSdcReplacementIdMap] =
    React.useState<Map<number, boolean>>(new Map<number, boolean>());

  const saveSdcReplacement = async (autoSdcReplacementId: number) => {
    setUpdatingSdcReplacementId(autoSdcReplacementId);

    await getJwtTokenFromIdToken()
      .then((token) => {
        // call updateSdcReplacement API
        return fetch(
          `${process.env.REACT_APP_API_ENDPOINT}/buildings/` +
            `${autoBuildingId}/sdc-replacements/${autoSdcReplacementId}`,
          {
            method: 'PATCH',
            headers: {
              Authorization: token,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              memo: props.sdcReplacements.find((sr) => {
                return sr.autoSdcReplacementId === autoSdcReplacementId;
              })?.memo,
            }),
          }
        );
      })
      .then((response) => {
        if (response.ok) {
          props.onSuccess('SDC交換履歴を更新しました。');
        } else {
          console.error(`HTTP error! status: ${response.status}`);
          props.onError(fetchErrorText);
        }
      })
      .catch((e) => {
        console.error(e);
        props.onError(fetchErrorText);
      })
      .finally(() => {
        setUpdatingSdcReplacementId(0);
        setEditedSdcReplacementIdMap((oldMap) => {
          const newMap = new Map(oldMap);
          newMap.set(autoSdcReplacementId, false);
          return newMap;
        });
      });
  };

  const columns: GridColDef[] = [
    {
      field: "createdAt",
      headerName: "日時",
      flex: 2,
      renderCell: (params: GridRenderCellParams<SdcReplacement>) => {
        const createdAtStr = new Date(
          params.row.createdAt
        ).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
        return (
        <Tooltip title={createdAtStr}>
          <span>{createdAtStr}</span>
        </Tooltip>
      )},
    },
    {
      field: "sourceSdcSerialNumber",
      headerName: "交換元SN",
      flex: 2,
      renderCell: (params: GridRenderCellParams<SdcReplacement>) => (
        <Tooltip
          title={
            `${params.row.sourceSdcSerialNumber} ` +
            `(UUID: ${params.row.sourceSdcUuid})`
          }
        >
          <span>{params.row.sourceSdcSerialNumber}</span>
        </Tooltip>
      ),
    },
    {
      field: "destinationSdcSerialNumber",
      headerName: "交換先SN",
      flex: 2,
      renderCell: (params: GridRenderCellParams<SdcReplacement>) => (
        <Tooltip
          title={
            `${params.row.destinationSdcSerialNumber} ` +
            `(UUID: ${params.row.destinationSdcUuid})`
          }
        >
          <span>{params.row.destinationSdcSerialNumber}</span>
        </Tooltip>
      ),
    },
    {
      field: "doorPlace",
      headerName: "場所",
      flex: 2,
      renderCell: (params: GridRenderCellParams<SdcReplacement>) => (
        <Tooltip
          title={params.row.doorPlace}
        >
          <span>{params.row.doorPlace}</span>
        </Tooltip>
      ),
    },
    {
      field: "memo",
      headerName: "メモ",
      flex: 4,
      renderCell: (params: GridRenderCellParams<SdcReplacement>) => (
        <TextField
          size="small"
          fullWidth
          defaultValue={params.row.memo}
          onBlur={(e) => {
            props.setSdcReplacements(
              props.sdcReplacements.map((sr) => {
                if (
                  sr.autoSdcReplacementId ===
                  params.row.autoSdcReplacementId
                ) {
                  return {
                    ...sr,
                    memo: e.target.value,
                  };
                }
                return sr;
              })
            );
            setEditedSdcReplacementIdMap((oldMap) => {
              const newMap = new Map(oldMap);
              newMap.set(params.row.autoSdcReplacementId, true);
              return newMap;
            });
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "操作",
      flex: 1,
      renderCell: (params: GridRenderCellParams<SdcReplacement>) => (
        <>
          {editedSdcReplacementIdMap.get(
            params.row.autoSdcReplacementId
          ) && (
            <Tooltip title="保存">
              <IconButton
                disabled={updatingSdcReplacementId !== 0}
                color="success"
                onClick={() => {
                  saveSdcReplacement(
                    params.row.autoSdcReplacementId
                  );
                }}
              >
                {updatingSdcReplacementId ===
                params.row.autoSdcReplacementId ? (
                  <CircularProgress size={20} />
                ) : (
                  <Save />
                )}
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    },
  ]

  return (
    <div style={{ height: 300, width: "100%" }}>
      <StyledDataGrid
        rows={props.sdcReplacements}
        columns={columns}
        getRowId={(row) => row.autoSdcReplacementId}
        disableRowSelectionOnClick
        localeText={
          props.sdcReplacements.length === 0
            ? customDataGridLocaleTextNoData
            : {}
        }
      />
    </div>
  );
};
